import DemoBadge from '@/components/feature/DemoBadge';

type Step = { n: string; icon: string; title: string; body: string };

const STEPS: Step[] = [
  {
    n: '01',
    icon: 'ri-radar-line',
    title: 'Discover',
    body: 'An agent scans an approved list of public sources — tournament listing pages, charity event calendars and club sites — for newly posted golf events around Southern California.',
  },
  {
    n: '02',
    icon: 'ri-file-list-3-line',
    title: 'Extract',
    body: 'The date, course, city, entry fee, organizer and charity beneficiary are pulled out and assembled into a structured draft event, ready to be checked.',
  },
  {
    n: '03',
    icon: 'ri-user-follow-line',
    title: 'Human review',
    body: 'Every draft lands in an editor\u2019s approval queue. GreenSide staff edit, correct or reject each one. This is a hard gate \u2014 nothing moves forward without a person.',
  },
  {
    n: '04',
    icon: 'ri-send-plane-line',
    title: 'Publish',
    body: 'Approved events go live on the tournament calendar and into the weekly digest. Unapproved drafts simply wait \u2014 no automatic publishing ever happens.',
  },
];

export default function AgentPipeline() {
  return (
    <section id="pipeline" className="bg-background-100 border-y border-background-200 px-4 md:px-8 py-16 md:py-24">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Tournament discovery</p>
        <DemoBadge label="Conceptual workflow" />
      </div>
      <h2 className="font-heading font-bold text-4xl md:text-6xl text-foreground-950 leading-[1.02]">From source to published</h2>
      <p className="mt-4 text-foreground-600 max-w-2xl">
        How a future agent would turn a public tournament listing into a reviewed, published event on GreenSide News.
      </p>

      <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {STEPS.map((s) => (
          <li key={s.n} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-md bg-foreground-950 text-accent-400 flex items-center justify-center font-heading font-extrabold text-xl shrink-0">
                {s.n}
              </span>
              <span className="w-8 h-8 flex items-center justify-center text-primary-500 text-2xl"><i className={s.icon}></i></span>
            </div>
            <h3 className="mt-5 font-heading font-bold text-2xl text-foreground-950 leading-tight">{s.title}</h3>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{s.body}</p>
          </li>
        ))}
      </ol>

      <p className="mt-8 flex items-start gap-3 text-sm text-foreground-700 bg-background-50 border border-background-200 border-l-4 border-l-accent-500 rounded-md px-4 py-4">
        <span className="w-5 h-5 flex items-center justify-center text-accent-700 shrink-0 mt-0.5"><i className="ri-shield-check-line"></i></span>
        <span><strong className="font-semibold text-foreground-950">Human-in-the-loop by design.</strong> Step 3 is a mandatory gate: no tournament reaches the calendar or the digest without an editor&apos;s explicit approval.</span>
      </p>
    </section>
  );
}